import EventParams = jwplayer.EventParams;
import {
  AnalyticPayloadV3,
  SeedConfig,
  sendBeaconV3
} from './beacon';
import { log } from './log';
import { getEntityFromURL } from '../utils/getEntityFromURL';
import { pixelsDataEnrichment } from './pixelsDataEnrichment';
import { createCustomParams } from './createCustomParams';
import { VideoProvider } from '../constants/Video';
import { dispatchEmbedEvent, metricsInitWithoutConsentCheck } from './metrics/loader';
import { getCookie, setCookie } from './metrics/utils';
import { v4 as uuidv4 } from 'uuid';
import { setBase } from '../utils/setBase';
import { pixelsUUIDKey } from './metrics/constants';

type PixelsSetupParam = {
  mediaId: string;
  playerId: string;
  jwplayerSiteId: string;
  pageOriginUrl: string;
  playlist: string;
  generateSEOMetadata: boolean;
  related: {
    file?: string;
    displayMode?: string;
  };
  skin: {
    name?: string;
  };
  setTimeEvents: boolean;
  isSwapped: boolean;
  platform: string;
};

const INDY_PIXELS_DOMAIN_SLUG = 'independent';

let playerInited = false;
let currentIndex = -1;
let nextIndex = 0;
let jwPlayerPlayerId: string;
let pixelsDataEnrichmentValues: { pixels_data: string; pixels_segments: string };
let previousAdTag: string;
let hasPassedSeventyFivePercentile = false;
let currentMediaIdUsedInAdtag: string;
let prebidCallback: (x: string) => Promise<string>;

const loadPlayerScript = (playerId: string, callback: any) => {
  playerInited = true;
  jwPlayerPlayerId = playerId;
  const script = document.createElement('script');
  script.defer = true;
  script.onload = callback;
  script.src = `https://cdn.jwplayer.com/libraries/${playerId}.js`;
  document.head.appendChild(script);
};

const setupWithRecommendationsAndTracking = async (domId: string, mediaId: string, options: PixelsSetupParam) => {
  const { jwplayer } = window;
  const base = __RELATED_VIDEO_ENDPOINT__;
  const originUrl = options.pageOriginUrl || document.location.href;
  const { id: pageId } = getEntityFromURL(originUrl);
  const recommendationsPlaylist = jwplayer.defaults?.related?.file || jwplayer(domId).getConfig()?.related?.file;

  if (recommendationsPlaylist) {
    const playlistUrl = encodeURIComponent(recommendationsPlaylist.replace('MEDIAID', mediaId));
    options.related = {
      file: `${base}/?playListUrl=${playlistUrl}&pageOriginUrl=${originUrl}`,
    };
  }
  options.playlist = `https://cdn.jwplayer.com/v2/media/${mediaId}`;
  jwplayer(domId).setup(options);

  // start pixels ad processing logic
  jwplayer(domId).on('time', (timeObject: { position: number; duration: number }) => {
    if (!hasPassedSeventyFivePercentile && timeObject.position >= timeObject.duration * 0.75) {
      hasPassedSeventyFivePercentile = true;
      prerunPrebid();
    }
  });

  const prerunPrebid = async () => {
    const nextMediaId = jwplayer(domId).getConfig().recItems[0].mediaid;
    log('pixels', 'prerunPrebid', 'nextMediaId', nextMediaId);
    pixelsDataEnrichmentValues = await pixelsDataEnrichment(nextMediaId, INDY_PIXELS_DOMAIN_SLUG);
    const prebidAdTag: string = await prebidCallback(nextMediaId);
    log('pixels', 'prerunPrebid', 'prebidCallback adTag returned', prebidAdTag);
    processNextVideoAdTagFromCallback(prebidAdTag, nextMediaId);
    currentMediaIdUsedInAdtag = nextMediaId; // record that this video has an ad tag...
  };

  jwplayer(domId).setPlaylistItemCallback(async (item: any, index: number) => {
    if (!pixels(domId).getAdTag()) {
      // abort if no ad tag
      return;
    }
    log('pixels', '====================================================');
    log('pixels', 'setPlaylistItemCallback', 'item (JW)', item);
    log('pixels', 'setPlaylistItemCallback', 'index (JW)', index);
    if (index < 0) {
      // account for weird jw behaviour (sometimes index=-1 and then repeats the same event with
      // index=0)
      return;
    }
    log('pixels', 'setPlaylistItemCallback', 'nextIndex', nextIndex);
    if (currentMediaIdUsedInAdtag !== item.mediaid) {
      // no ad tag yet for this video, so run prebid, etc.
      pixelsDataEnrichmentValues = await pixelsDataEnrichment(item.mediaid, INDY_PIXELS_DOMAIN_SLUG);
      if (nextIndex > 0) {
        const prebidAdTag: string = await prebidCallback(item.mediaid);
        log('pixels', 'setPlaylistItemCallback', 'prebidCallback adTag returned', prebidAdTag);
        processNextVideoAdTagFromCallback(prebidAdTag, item.mediaid);
      }
    }
    currentIndex++;
    nextIndex++;
    hasPassedSeventyFivePercentile = false; // reset for next video
    log('pixels', 'setPlaylistItemCallback', 'currentIndex', currentIndex);
  });

  const processNextVideoAdTagFromCallback = (prebidAdTag: string, mid: string) => {
    let adTag;
    if (!prebidAdTag) {
      if (jwplayer(domId).getConfig().advertising?.schedule?.[0]?.tag) {
        adTag = jwplayer(domId).getConfig().advertising.schedule[0].tag;
      } else if (jwplayer.defaults?.advertising?.schedule?.[0]?.tag) {
        adTag = jwplayer.defaults.advertising.schedule[0].tag;
      }
    } else {
      adTag = prebidAdTag;
    }
    enrichAndSetAdTag(domId, adTag, mid);
  };

  // end pixels ad processing logic

  // load the video-metrics
  try {
    // START_HACK: hack for indy, initiate the pixelsLayer
    // Indy page view event is dispatch by GTM (backend integration)
    let uuid: string = getCookie(pixelsUUIDKey);

    if (!uuid) {
      uuid = uuidv4();
      setCookie(pixelsUUIDKey, uuid, 365);
    }

    const urlParts = location.pathname.match(/(\d+).html/);
    const basePayload = setBase(
      'independent', uuid,
      window.JSGlobals?.article?.videoId,
      Array.isArray(urlParts) && urlParts.length > 1 ? urlParts[1] : null,
    );

    (window as { [key: string]: any }).pixelsLayer = {
      push: (data: AnalyticPayloadV3 | SeedConfig) => {
        return sendBeaconV3({
          ...basePayload,
          ...data
        } as AnalyticPayloadV3);
      },
    };
    // END_HACK

    dispatchEmbedEvent(options.isSwapped ? mediaId : null);
    await metricsInitWithoutConsentCheck(domId, VideoProvider.JWPLAYER, pageId);
  } catch (e) {
    console.error(e);
  }
};

const extractFromAdTag = (key: string, adTag: string, isCustomParams = false) => {
  const parts = new URL(adTag);
  const urlParams = new URLSearchParams(parts.search);
  if (!isCustomParams) {
    return urlParams.get(key);
  }
  if (urlParams.has('cust_params')) {
    const customParams = createCustomParams(urlParams.get('cust_params'));
    return customParams.getValuesForKey(key);
  }
  return '';
};

const enrichAndSetAdTag = (domId: string, adTag: string, mediaId: string = null, setByPixels = false) => {
  const { jwplayer } = window;
  if (!mediaId) {
    // if no mediaId passed in, retrieve the current playing video's mediaId
    mediaId = jwplayer(domId).getConfig().playlistItem.mediaid;
  }
  log('pixels', 'enrichAndSetAdTag', 'media id', mediaId);

  const parts = new URL(adTag);
  const base = parts.origin + parts.pathname;
  const urlParams = new URLSearchParams(parts.search);

  if (urlParams.has('cust_params')) {
    const customParams = createCustomParams(urlParams.get('cust_params'));
    if (pixelsDataEnrichmentValues?.pixels_data) {
      customParams.updateOrAdd('pixels_data', pixelsDataEnrichmentValues.pixels_data);
    }
    if (pixelsDataEnrichmentValues?.pixels_segments) {
      customParams.updateOrAdd('pixels_segments', pixelsDataEnrichmentValues.pixels_segments);
    }
    if (setByPixels) {
      customParams.updateOrAdd('adpos', nextIndex);
      customParams.updateOrAdd('videoID', mediaId);
      customParams.updateOrAdd('playerid', jwPlayerPlayerId);
      customParams.updateOrAdd('permutive', extractFromAdTag('permutive', previousAdTag, true));
      // add missing description_url
      urlParams.set('description_url', '__page-url__');
      // copy consent from first play to second play+
      urlParams.set('gdpr', extractFromAdTag('gdpr', previousAdTag));
      urlParams.set('gdpr_consent', extractFromAdTag('gdpr_consent', previousAdTag));
      urlParams.set('addtl_consent', extractFromAdTag('addtl_consent', previousAdTag));
      log('pixels', 'enrichAndSetAdTag', 'prev ad tag is: ', previousAdTag);
    }
    urlParams.set('cust_params', customParams.get());
  }

  const tag = `${base}?${urlParams.toString()}`;
  if (setByPixels) {
    log('pixels', 'enrichAndSetAdTag', 'setAdTag called BY PIXELS', tag);
    log('pixels', 'enrichAndSetAdTag', 'adpos set to', nextIndex);
  } else {
    previousAdTag = tag;
    log('pixels', 'enrichAndSetAdTag', 'setAdTag called', tag);
  }
  if (jwplayer(domId).getConfig().advertising?.schedule?.[0]?.tag) {
    jwplayer(domId).getConfig().advertising.schedule[0].tag = tag;
  } else if (jwplayer.defaults?.advertising?.schedule?.[0]?.tag) {
    jwplayer.defaults.advertising.schedule[0].tag = tag;
  }
};

const addLogo = (el: any, logo: string) => {
  const img = document.createElement('img');
  img.src = logo;
  img.style.position = 'absolute';
  img.style.left = '50%';
  img.style.top = '50%';
  img.style.width = '10%';
  img.style.marginLeft = '-5%';
  img.style.marginTop = '-5%';
  el.appendChild(img);
};

export default function pixels(domId: string) {
  return {
    initPlayer: (playerId: string, callback: any, bgcolor: string, logo: string) => {
      log('pixels', 'wrapper-independent-prebid', 'initPlayer', 'called with playerId', playerId, bgcolor, logo);
      loadPlayerScript(playerId, callback);
      const el = document.getElementById(domId);
      el.style.position = 'relative';
      el.style.width = '100%';
      el.style.paddingTop = '56.25%';
      if (bgcolor) {
        el.style.background = bgcolor;
      }
      if (logo) {
        addLogo(el, logo);
      }
    },
    setup: (options: PixelsSetupParam) => {
      const playerId = options.playerId;
      const mediaId = options.mediaId;
      if (playerInited) {
        log('pixels', 'setup', 'called with mediaId', mediaId);
        setupWithRecommendationsAndTracking(domId, mediaId, options);
      } else {
        log('pixels', 'setup', 'called with mediaId and playerId', mediaId, playerId);
        loadPlayerScript(playerId, () => {
          setupWithRecommendationsAndTracking(domId, mediaId, options);
        });
      }
    },
    test: (params: any) => {
      log('pixels', 'test', 'called', params);
    },
    initPlaylistPrebidCallback: (callback: (x: string) => Promise<string>) => {
      log('pixels', 'initPlaylistPrebidCallback', 'called');
      prebidCallback = callback;
    },
    getVideoPlayerInstance: () => {
      const { jwplayer } = window;
      return jwplayer;
    },
    on: (event: string, func: any) => {
      log('pixels', 'on', 'inited for event', event);
      const { jwplayer } = window;
      jwplayer(domId).on(event as keyof EventParams, func);
    },
    resize: (width: number, height: number) => {
      log('pixels', 'resize', 'called');
      const { jwplayer } = window;
      jwplayer(domId).resize(width, height);
    },
    getHeight: (): number => {
      log('pixels', 'getHeight', 'called');
      const { jwplayer } = window;
      return jwplayer(domId).getHeight();
    },
    getAdTag: (): string => {
      log('pixels', 'getAdTag', 'called');
      const { jwplayer } = window;
      if (jwplayer(domId).getConfig().advertising?.schedule?.[0]?.tag) {
        return jwplayer(domId).getConfig().advertising.schedule[0].tag;
      } else if (jwplayer.defaults?.advertising?.schedule?.[0]?.tag) {
        return jwplayer.defaults.advertising.schedule[0].tag;
      }
    },
    setAdTag: (adTag: string) => {
      // only allow tag to be set on first video...
      log('pixels', 'setAdTag', 'currentIndex', currentIndex);
      if (!adTag || currentIndex > 0) return;
      enrichAndSetAdTag(domId, adTag);
    },
    getMediaId: (): string => {
      log('pixels', 'getMediaId', 'called');
      const { jwplayer } = window;
      return jwplayer(domId).getConfig().playlistItem.mediaid;
    },
    getPlaylistIndex: (): number => {
      log('pixels', 'getPlaylistIndex', 'called');
      const { jwplayer } = window;
      return jwplayer(domId).getPlaylistIndex();
    },
    getPlaylistItem: (index: number) => {
      log('pixels', 'getPlaylistItem', 'called');
      const { jwplayer } = window;
      return jwplayer(domId).getPlaylistItem(index);
    },
    trigger: (name: string, data: any) => {
      log('pixels', 'trigger', 'called');
      const { jwplayer } = window;
      return jwplayer(domId).trigger(name as keyof EventParams, data);
    },
    getConfig: () => {
      log('pixels', 'getConfig', 'called');
      const { jwplayer } = window;
      return jwplayer(domId).getConfig();
    },
    pause: () => {
      log('pixels', 'pause', 'called');
      const { jwplayer } = window;
      return jwplayer(domId).pause();
    },
    play: () => {
      log('pixels', 'play', 'called');
      const { jwplayer } = window;
      return jwplayer(domId).play();
    },
  };
}
